import { useState, useSyncExternalStore } from "react";

import { cn } from "../../cn.ts";
import { useDesignSystemCopy } from "../../language/use-design-system-copy.ts";
import { Button } from "../button.tsx";
import type { Tone } from "../feedback.tsx";
import { Icon } from "../icon.tsx";
import type { IconName } from "../icon.tsx";

// Toasts: a tiny module-level store so any code can announce an outcome. A
// toast leaves on its own after a while; hovering the stack holds them all.
// Split out of feedback.tsx (size-ceilings) once a sticky toast with an
// action (ADR-030) joined the ordinary kind.

type ToastEntry = {
  readonly id: number;
  readonly tone: Tone;
  readonly title: string;
  readonly description?: string;
  readonly action?: { readonly label: string; readonly onClick: () => void };
  /** Never leaves on its own; only the action or the dismiss button closes it (a release toast
   * the person must act on, ADR-030). */
  readonly sticky?: boolean;
};

const TOAST_MS = 4500;

let toasts: readonly ToastEntry[] = [];
let nextId = 1;
let held = false;
const toastListeners = new Set<() => void>();
const timers = new Map<
  number,
  { remaining: number; started: number; handle?: ReturnType<typeof setTimeout> }
>();

function emitToasts(): void {
  for (const listener of toastListeners) listener();
}

function dismiss(id: number): void {
  const timer = timers.get(id);
  if (timer?.handle) clearTimeout(timer.handle);
  timers.delete(id);
  toasts = toasts.filter((item) => item.id !== id);
  emitToasts();
}

function run(id: number): void {
  const timer = timers.get(id);
  if (!timer) return;
  timer.started = Date.now();
  timer.handle = setTimeout(() => dismiss(id), timer.remaining);
}

function hold(on: boolean): void {
  if (held === on) return;
  held = on;
  for (const [id, timer] of timers) {
    if (on) {
      if (timer.handle) clearTimeout(timer.handle);
      timer.remaining = Math.max(timer.remaining - (Date.now() - timer.started), 800);
    } else run(id);
  }
}

export function toast(entry: {
  tone?: Tone;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  sticky?: boolean;
}): void {
  const id = nextId++;
  toasts = [
    ...toasts,
    {
      id,
      tone: entry.tone ?? "neutral",
      title: entry.title,
      ...(entry.description ? { description: entry.description } : {}),
      ...(entry.action ? { action: entry.action } : {}),
      ...(entry.sticky ? { sticky: true } : {}),
    },
  ];
  // A sticky toast gets no timer at all, so `run` and `hold` (which only ever look a toast's
  // timer up by id) leave it alone: it waits for its action or the dismiss button, never a clock.
  if (!entry.sticky) {
    timers.set(id, { remaining: TOAST_MS, started: Date.now() });
    if (!held) run(id);
  }
  emitToasts();
}

// A toast's anatomy is fixed here, not reinvented per caller: one row, `content` (an optional
// tone icon, then title and description stacked) filling the width, `actions` (the action button,
// then dismiss) shrink-wrapped at the end and vertically centred on `content`. `toast()` takes
// data; nothing a caller passes can move a slot, change its spacing or its type (PR #147 found
// the opposite: a hand-rolled action button, stacked under the title, invented its own padding
// and text size). D997-D999 record the measurements against Sergio's reference.
const TOAST_CARD =
  "flex animate-[toast-in_var(--motion-slow)_var(--motion-ease)] items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-lg";
const TOAST_CONTENT = "flex min-w-0 flex-1 flex-col gap-1";
const TOAST_TITLE = "text-sm font-medium text-fg";
const TOAST_DESCRIPTION = "text-sm leading-5 text-fg-muted";
const TOAST_ACTIONS = "flex shrink-0 items-center gap-2 self-center";
const TOAST_WIDTH = "w-[min(400px,calc(100vw-2rem))]";

/** The tone itself is the point only for these; an info or a neutral toast reads fine from its
 * words alone, so it carries no icon (D998). */
const ICON_TONES: ReadonlySet<Tone> = new Set(["success", "warning", "danger"]);

const TOAST_ICON: Readonly<Record<Tone, IconName>> = {
  neutral: "info",
  primary: "sparkles",
  success: "check-circle",
  warning: "alert-triangle",
  danger: "alert-circle",
  info: "info",
};

const TOAST_ICON_TONE: Readonly<Record<Tone, string>> = {
  neutral: "text-fg-muted",
  primary: "text-fg",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
};

const GAP = 8;
const PEEK = 10;
const VISIBLE = 3;

function subscribeToasts(listener: () => void) {
  toastListeners.add(listener);
  return () => {
    toastListeners.delete(listener);
  };
}
const readToasts = () => toasts;

/**
 * Mounted once by the app shell. The newest toast sits in front and older ones
 * peek out behind it; hovering fans the stack out and holds every timer.
 */
export function Toaster() {
  const t = useDesignSystemCopy();
  const items = useSyncExternalStore(subscribeToasts, readToasts, readToasts);
  const [expanded, setExpanded] = useState(false);
  const [heights, setHeights] = useState<Readonly<Record<number, number>>>({});
  const newestFirst = items.toReversed();
  const heightOf = (id: number) => heights[id] ?? 64;
  const front = newestFirst[0] ? heightOf(newestFirst[0].id) : 0;

  const measure = (id: number) => (element: HTMLDivElement | null) => {
    if (!element) return;
    const height = element.offsetHeight;
    setHeights((current) => (current[id] === height ? current : { ...current, [id]: height }));
  };

  const setOpen = (open: boolean) => {
    setExpanded(open);
    hold(open);
  };

  // Where each toast sits when fanned out: the heights of the newer ones above it.
  const stackedAt = newestFirst.map((_, index) =>
    newestFirst.slice(0, index).reduce((sum, newer) => sum + heightOf(newer.id) + GAP, 0),
  );
  const placed = newestFirst.map((item, index) => ({
    item,
    index,
    y: expanded ? (stackedAt[index] ?? 0) : index * PEEK,
  }));
  const total = newestFirst.reduce((sum, item) => sum + heightOf(item.id) + GAP, 0) - GAP;

  if (items.length === 0) return null;
  return (
    <section
      aria-live="polite"
      aria-label={t("notifications")}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      className={cn(
        "fixed right-4 bottom-4 z-[60] transition-[height] duration-(--motion-slow) ease-glide",
        TOAST_WIDTH,
      )}
      style={{ height: expanded ? total : front + Math.min(items.length - 1, VISIBLE - 1) * PEEK }}
    >
      {placed.map(({ item, index, y }) => {
        const behind = !expanded && index > 0;
        return (
          <div
            key={item.id}
            ref={measure(item.id)}
            className="group absolute right-0 bottom-0 w-full origin-bottom transition-[transform,opacity] duration-(--motion-slow) ease-glide"
            style={{
              transform: `translateY(-${y}px) scale(${expanded ? 1 : 1 - index * 0.05})`,
              opacity: !expanded && index >= VISIBLE ? 0 : 1,
              zIndex: items.length - index,
              ...(behind ? { height: front, overflow: "hidden" } : {}),
            }}
          >
            <div className={cn(TOAST_CARD, behind && "opacity-0")}>
              {ICON_TONES.has(item.tone) ? (
                <span className={cn("self-center [&_svg]:size-4", TOAST_ICON_TONE[item.tone])}>
                  <Icon name={TOAST_ICON[item.tone]} />
                </span>
              ) : null}
              <div className={TOAST_CONTENT}>
                <p className={TOAST_TITLE}>{item.title}</p>
                {item.description ? <p className={TOAST_DESCRIPTION}>{item.description}</p> : null}
              </div>
              <div className={TOAST_ACTIONS}>
                {item.action ? (
                  <Button variant="secondary" size="md" onClick={item.action.onClick}>
                    {item.action.label}
                  </Button>
                ) : null}
                <span
                  className={cn(
                    "transition-opacity",
                    // D999: a sticky toast's close stays visible outright, at the same weight as
                    // the action's own label; it is the only way to put one away without acting,
                    // since a sticky toast never leaves on its own. An ordinary toast's close
                    // only shows on hover, so the stack reads clean until someone means to act.
                    item.sticky
                      ? "opacity-100"
                      : "opacity-0 group-hover:opacity-100 focus-within:opacity-100",
                  )}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("dismiss")}
                    onClick={() => dismiss(item.id)}
                  >
                    <Icon name="x" />
                  </Button>
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}
